import { SectionBox } from '@shared/components/ui/SectionBox';
import { useTheme } from '@shared/components/ui/theme-provider';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrderDeliveryRate } from '../../hooks/useOrderDeliveryRate';

export const DeliveryRate: FC = () => {
  const { t } = useTranslation('viewOrdersModule');
  const { theme } = useTheme();
  const { data, isLoading } = useOrderDeliveryRate();

  const delivered = data?.delivered ?? 0;
  const returned = data?.returned ?? 0;
  const hasHistory = delivered + returned > 0;

  const percentage = useMemo(() => {
    if (typeof data?.delivery_rate !== 'number') return null;
    return Math.max(0, Math.min(100, data.delivery_rate));
  }, [data?.delivery_rate]);

  const formattedPercentage = useMemo(() => {
    if (percentage === null) return null;
    return Number.isInteger(percentage) ? String(percentage) : percentage.toFixed(2);
  }, [percentage]);

  const isLightTheme = useMemo(() => {
    if (theme === 'light') return true;
    if (theme === 'dark') return false;
    return typeof window !== 'undefined' && !window.matchMedia('(prefers-color-scheme: dark)').matches;
  }, [theme]);

  const percentageTextClass = useMemo(() => {
    if (isLightTheme && percentage !== null && percentage >= 75) {
      return 'text-white';
    }
    return 'text-title';
  }, [isLightTheme, percentage]);

  return (
    <SectionBox className="bg-card-bg">
      {/* Header de estadísticas - Statistics header */}
      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-bold">{t('sections.delivery-rate.title')}</h2>

        {/* Barra de progreso de entregas - Deliveries progress bar */}
        <div className="relative h-7 w-full overflow-hidden rounded-full bg-hover-press">
          {isLoading ? (
            <div className="h-full w-0 animate-pulse rounded-full bg-primary" />
          ) : percentage !== null ? (
            <>
              <div className="h-full rounded-full bg-primary" style={{ width: `${percentage}%` }} />
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-2">
                <span className={`whitespace-nowrap text-xs font-semibold ${percentageTextClass}`}>
                  {t('sections.delivery-rate.percentage', { percentage: formattedPercentage })}
                </span>
              </div>
            </>
          ) : (
            <div className="flex h-full items-center justify-center px-3">
              <span className="text-xs font-medium text-subtext">{t('sections.delivery-rate.no-history')}</span>
            </div>
          )}
        </div>

        {/* Resumen numérico - Numeric summary */}
        {hasHistory && (
          <p className="text-sm font-medium text-text">
            {t('sections.delivery-rate.summary', { delivered, returned })}
          </p>
        )}
      </div>
    </SectionBox>
  );
};
